import type YouTube from "@vip30/react-youtube";
import type { FunctionComponent, ReactNode } from "react";
import { createContext, useCallback, useContext, useRef, useState } from "react";

import { useDeviceDetect } from "shared/hooks/device-detect";
import { notEmpty } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";

type ContextValue = {
  closePlayerModal?: () => Promise<void>;
  modalState?: ModalState;
  playVideoShort?: (entityId: string, activeSlide: number, youtubeId?: null | string) => void;
  registerPlayer?: (entityId: string, reference: React.RefObject<null | YouTube>) => void;
  sendVideoSysTracking?: (actionType: "close" | "mute" | "next") => Promise<void>;
  setPlayerDimension?: (entityId: string, dimension: string) => void;
  toggleMuteVideo?: () => Promise<void>;
  unregisterPlayer?: (entityId: string) => void;
  updateModalState?: (entityId: string, activeSlide: number) => void;
};

type ModalState = {
  activeEntityId?: string;
  activeSlide: number;
  activeYoutubeId?: null | string;
  isOpen: boolean;
  mute?: boolean;
};

type Props = {
  children: ReactNode;
};

type YouTubeInternalPlayer = {
  getCurrentTime: () => Promise<number>;
};

const VideoShortCarouselContext = createContext<ContextValue>({});

export const VideoShortCarouselContextProvider: FunctionComponent<Props> = ({ children }) => {
  const playerReferencesMap = useRef<Map<string, React.RefObject<null | YouTube>>>(new Map());
  const playerDimensionsMap = useRef<Map<string, string>>(new Map());
  const { isIOS } = useDeviceDetect();

  const [modalState, setModalState] = useState<ModalState>({
    activeEntityId: undefined,
    activeSlide: -1,
    activeYoutubeId: undefined,
    isOpen: false,
    mute: isIOS,
  });

  const registerPlayer = (entityId: string, reference: React.RefObject<null | YouTube>) => {
    playerReferencesMap.current.set(entityId, reference);
  };

  const unregisterPlayer = (entityId: string) => {
    playerReferencesMap.current.delete(entityId);
    playerDimensionsMap.current.delete(entityId);
  };

  const setPlayerDimension = useCallback((entityId: string, dimension: string) => {
    playerDimensionsMap.current.set(entityId, dimension);
  }, []);

  const updateModalState = useCallback(
    (entityId: string, activeSlide: number, isOpen?: boolean, youtubeId?: null | string) => {
      setModalState(previous => ({
        ...previous,
        activeEntityId: entityId,
        activeSlide,
        activeYoutubeId: youtubeId,
        isOpen: isOpen ?? previous.isOpen,
      }));
    },
    [],
  );

  const sendVideoSysTracking = useCallback(
    async (actionType: "close" | "mute" | "next") => {
      const { activeEntityId, activeYoutubeId } = modalState;
      if (!notEmpty(activeEntityId)) return;

      const player = playerReferencesMap.current.get(activeEntityId)?.current?.internalPlayer as
        | undefined
        | YouTubeInternalPlayer;
      const time = await player?.getCurrentTime();

      sendGA4Tracking({
        action: "sys",
        category: "video",
        customized_parameters: {
          action_type: actionType,
          dimension: playerDimensionsMap.current.get(activeEntityId) ?? "",
          player_name: "youtube_pfp",
          position: "scmp_shorts",
          time: Math.round(time ?? 0),
          video_id: activeEntityId,
          youtube_id: activeYoutubeId ?? "",
        },
        subcategory: "play",
      });
    },
    [modalState],
  );

  const playVideoShort = useCallback(
    (entityId: string, activeSlide: number, youtubeId?: null | string) => {
      updateModalState(entityId, activeSlide, true, youtubeId);
      sendGA4Tracking({
        action: "click",
        category: "video",
        customized_parameters: {
          player_name: "youtube_pfp",
          video_id: entityId,
          widget_name: "scmp_shorts",
          youtube_id: youtubeId ?? "",
        },
        subcategory: "widget",
      });
    },
    [updateModalState],
  );

  const closePlayerModal = useCallback(async () => {
    await sendVideoSysTracking("close");

    setModalState(previous => ({
      activeEntityId: undefined,
      activeSlide: -1,
      isOpen: false,
      mute: previous.mute,
    }));
  }, [sendVideoSysTracking]);

  const toggleMuteVideo = useCallback(async () => {
    if (!modalState.mute) {
      await sendVideoSysTracking("mute");
    }
    setModalState(previous => ({ ...previous, mute: !previous.mute }));
  }, [modalState.mute, sendVideoSysTracking]);

  return (
    <VideoShortCarouselContext.Provider
      value={{
        closePlayerModal,
        modalState,
        playVideoShort,
        registerPlayer,
        sendVideoSysTracking,
        setPlayerDimension,
        toggleMuteVideo,
        unregisterPlayer,
        updateModalState,
      }}
    >
      {children}
    </VideoShortCarouselContext.Provider>
  );
};

export const useVideoShortCarouselContext = () => useContext(VideoShortCarouselContext);
